import { Link, useNavigate } from "react-router-dom";
import {
  Shield,
  FileText,
  Calendar,
  MessageCircle,
  Phone,
  LogOut,
  ChevronRight,
  ChevronDown,
  Users,
  Home,
  ShoppingBag,
  LogIn,
  Trash,
  GraduationCap,
  UserCheck,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "../hooks/use-mobile";
import MainLayout from "../components/MainLayout";
import { isAuthenticated } from "../utils/helpers";

const ExploreMore = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const isUserLoggedIn = isAuthenticated();

  const [isParentsOpen, setIsParentsOpen] = useState(false);
  const [isCollegeStudentsOpen, setIsCollegeStudentsOpen] = useState(false);

  const personalItems = [
    {
      icon: <Shield className="w-5 h-5 text-gray-500" />,
      title: "Privacy Policy",
      path: "/privacy",
    },
    {
      icon: <FileText className="w-5 h-5 text-gray-500" />,
      title: "Terms and Conditions",
      path: "/toc",
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-gray-500" />,
      title: "Child Safety Policy",
      path: "/child-safety",
    },
  ];

  function handleUserLogout() {
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem("loggedout", true);
    navigate("/signin");
  }

  const additionalItems = [
    {
      icon: <Calendar className="w-5 h-5 text-gray-500" />,
      title: "Events",
      path: "/events",
    },
    {
      icon: <MessageCircle className="w-5 h-5 text-gray-500" />,
      title: "Make Yari",
      path: "/makeyaari-onboarding",
    },
    {
      icon: <Users className="w-5 h-5 text-gray-500" />,
      title: "Add Groups",
      path: "/groups/create",
    },
    {
      icon: <ShoppingBag className="w-5 h-5 text-gray-500" />,
      title: "Classified",
      path: "/classifieds",
    },
    {
      icon: <Home className="w-5 h-5 text-gray-500" />,
      title: "Roommate",
      path: "/classifieds?category=Roommate",
    },
  ];

  const contactAndAuthItems = [
    {
      icon: <Phone className="w-5 h-5 text-gray-500" />,
      title: "Contact Us",
      path: "/contact",
    },
    {
      icon: !isUserLoggedIn ? (
        <LogIn className="w-5 h-5 text-gray-500" />
      ) : (
        <LogOut className="w-5 h-5 text-gray-500" />
      ),
      title: !isUserLoggedIn ? "Login" : "Logout",
      path: !isUserLoggedIn ? "/signin" : "/logout",
    },
    {
      icon: isUserLoggedIn && <Trash className="w-5 h-5 text-gray-500" />,
      title: isUserLoggedIn && "Delete Account",
      path: isUserLoggedIn && "/account/delete",
    },
  ];

  return (
    <MainLayout rs={false}>
      <div className="p-4 font-afacad">
        <h1 className="text-2xl font-bold mb-6 font-fraunces">Explore More</h1>

        <div
          className={`${!isMobile ? "grid grid-cols-2 gap-8" : "space-y-6"}`}
        >
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-2 font-fraunces">
              Personal
            </h2>
            <div className="bg-white rounded-lg shadow-sm">
              {personalItems.map((item, index) => (
                <Link to={item.path} key={index}>
                  <div className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span>{item.title}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-2 font-fraunces">
              Additional
            </h2>
            <div className="bg-white rounded-lg shadow-sm">
              {additionalItems.map((item, index) => (
                <Link to={item.path} key={index}>
                  <div className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span>{item.title}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>
              ))}

              <div className="">
                <div
                  onClick={() => setIsParentsOpen(!isParentsOpen)}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <UserCheck className="w-5 h-5 text-gray-500" />
                    <span>Parents</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isParentsOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
                {isParentsOpen && (
                  <div className="bg-gray-50">
                    <Link to="/parents/highschoolers">
                      <div className="flex items-center justify-between p-4 pl-12 hover:bg-gray-100 cursor-pointer">
                        <span>High Schoolers</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  </div>
                )}
              </div>

              <div className="">
                <div
                  onClick={() =>
                    setIsCollegeStudentsOpen(!isCollegeStudentsOpen)
                  }
                  className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <GraduationCap className="w-5 h-5 text-gray-500" />
                    <span>College Students</span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isCollegeStudentsOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
                {isCollegeStudentsOpen && (
                  <div className="bg-gray-50">
                    <Link to="">
                      <div className="flex items-center justify-between p-4 pl-12 hover:bg-gray-100 cursor-pointer">
                        <span>Career Guidance</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                    <Link to="">
                      <div className="flex items-center justify-between p-4 pl-12 hover:bg-gray-100 cursor-pointer">
                        <span>Mentorship</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                    <Link to="">
                      <div className="flex items-center justify-between p-4 pl-12 hover:bg-gray-100 cursor-pointer">
                        <span>Internship</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </Link>
                  </div>
                )}
              </div>

              {contactAndAuthItems.map((item, index) => {
                if (!item.title) return null;

                const isLogout = item.title === "Logout";

                return isLogout ? (
                  <div
                    key={`contact-${index}`}
                    onClick={handleUserLogout}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      <span>{item.title}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                ) : (
                  <Link to={item.path} key={`contact-${index}`}>
                    <div className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                      <div className="flex items-center gap-3">
                        {item.icon}
                        <span>{item.title}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ExploreMore;
